import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, AlertCircle } from 'lucide-react';

// Version key — bump when the notice content changes so previously-dismissed users see it again
const NOTICE_KEY = 'sunsari_notice_maintenance_20260709';

const MaintenanceNotice = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Hide automatically after the reopening date (July 10, 2026)
    const now = new Date();
    const expiresAt = new Date('2026-07-11T00:00:00');
    if (now >= expiresAt) return;

    try {
      if (!localStorage.getItem(NOTICE_KEY)) setVisible(true);
    } catch {
      setVisible(true);
    }
  }, []);

  // Push TopBar / MainNav / page below the fixed banner while it is visible
  useEffect(() => {
    if (visible) {
      document.body.style.setProperty('--notice-h', '58px');
      document.body.classList.add('has-notice');
    } else {
      document.body.style.removeProperty('--notice-h');
      document.body.classList.remove('has-notice');
    }
  }, [visible]);

  const dismiss = () => {
    try { localStorage.setItem(NOTICE_KEY, '1'); } catch {}
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.35 }}
          className="fixed top-0 left-0 right-0 z-[10001] bg-gradient-to-r from-[#1A1A1A] via-[#2F2F2F] to-[#1A1A1A] border-b border-[#C97A2B]/40 overflow-hidden"
          data-testid="maintenance-notice"
        >
          <div className="absolute -top-4 -right-8 w-40 h-40 bg-[#C97A2B]/15 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute -bottom-8 left-1/4 w-56 h-56 bg-[#B65A3A]/10 rounded-full blur-3xl pointer-events-none" />

          <div className="relative max-w-7xl mx-auto px-4 py-2.5 md:py-3 flex items-center gap-3 md:gap-5">
            <div className="hidden sm:flex w-9 h-9 rounded-full bg-[#C97A2B]/20 border border-[#C97A2B]/40 items-center justify-center flex-shrink-0">
              <AlertCircle className="w-4 h-4 text-[#C97A2B]" />
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                <span className="text-[#C97A2B] text-[10px] md:text-[11px] tracking-[0.25em] uppercase font-bold">
                  Notice
                </span>
                <span className="text-white text-[13px] md:text-[15px] leading-snug">
                  We're closed on <strong className="text-[#C97A2B]">Thursday 09.07</strong> for maintenance.
                  <span className="text-white/80"> Back to normal on <strong className="text-white">Friday 10.07</strong>.</span>
                  <span className="hidden md:inline text-white/60"> — Sorry for the inconvenience.</span>
                </span>
              </div>
              <p className="md:hidden text-white/50 text-[10px] mt-0.5">
                Sorry for the inconvenience.
              </p>
            </div>

            <button
              onClick={dismiss}
              aria-label="Dismiss notice"
              className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 transition-colors"
              data-testid="dismiss-notice-btn"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default MaintenanceNotice;
